import { supabase } from '@common/api/authService';

export type WorkspaceManagedSkill = {
    id: string;
    workspaceId: string;
    skillKey: string;
    displayName: string;
    description: string | null;
    versionSeq: number;
    createdAt: string;
    updatedAt: string;
    createdByName: string | null;
    createdByEmail: string | null;
};

type WorkspaceSkillsResponse = {
    page?: unknown;
    page_size?: unknown;
    skills?: unknown;
    total?: unknown;
    total_pages?: unknown;
};

export type WorkspaceManagedSkillsPage = {
    page: number;
    pageSize: number;
    skills: WorkspaceManagedSkill[];
    total: number;
    totalPages: number;
};

type WorkspaceSkillDetailResponse = {
    skill_id?: unknown;
    skill_md?: unknown;
    version_seq?: unknown;
};

export type WorkspaceManagedSkillDetail = {
    skillId: string;
    skillMarkdown: string;
    versionSeq: number;
};

const asRecord = (value: unknown): Record<string, unknown> | null => (
    value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null
);

const asString = (value: unknown): string | null => (
    typeof value === 'string' && value.trim() ? value.trim() : null
);

const asPositiveNumber = (value: unknown): number | null => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const asNonNegativeNumber = (value: unknown): number | null => {
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

export class WorkspaceSkillsPermissionError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'WorkspaceSkillsPermissionError';
    }
}

const normalizeManagedSkill = (value: unknown): WorkspaceManagedSkill | null => {
    const record = asRecord(value);
    if (!record) return null;

    const id = asString(record.id);
    const workspaceId = asString(record.workspace_id);
    const skillKey = asString(record.skill_key);
    const displayName = asString(record.display_name);
    const versionSeq = asPositiveNumber(record.version_seq);
    const createdAt = asString(record.created_at);
    const updatedAt = asString(record.updated_at);
    if (!id || !workspaceId || !skillKey || !displayName || !versionSeq || !createdAt || !updatedAt) {
        return null;
    }

    return {
        id,
        workspaceId,
        skillKey,
        displayName,
        description: asString(record.description),
        versionSeq,
        createdAt,
        updatedAt,
        createdByName: asString(record.creator_name),
        createdByEmail: asString(record.creator_email),
    };
};

async function invokeWorkspaceSkillsFunction<T>(body: Record<string, unknown>): Promise<T> {
    const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '');
    const anonKey = String(import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim();
    if (!supabaseUrl || !anonKey) {
        throw new Error('Bustly cloud configuration is unavailable.');
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
        throw new Error('Sign in is required.');
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/user-skills-sync`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            apikey: anonKey,
        },
        body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => null) as unknown;
    if (!response.ok) {
        const record = asRecord(payload);
        const details = asRecord(record?.details);
        const message = asString(details?.message) || asString(record?.error) || asString(record?.details) || response.statusText;
        if (response.status === 401 || response.status === 403) {
            throw new WorkspaceSkillsPermissionError(message || 'Workspace skill management permission is required.');
        }
        throw new Error(message || `Workspace skill request failed (${response.status}).`);
    }
    return payload as T;
}

export async function listWorkspaceManagedSkills(params: {
    page: number;
    pageSize: number;
    workspaceId: string;
}): Promise<WorkspaceManagedSkillsPage> {
    const payload = await invokeWorkspaceSkillsFunction<WorkspaceSkillsResponse>({
        action: 'manage_list',
        page: params.page,
        page_size: params.pageSize,
        workspace_id: params.workspaceId,
    });
    const skills = Array.isArray(payload.skills) ? payload.skills
        .map(normalizeManagedSkill)
        .filter((skill): skill is WorkspaceManagedSkill => Boolean(skill)) : [];
    const page = asPositiveNumber(payload.page) ?? params.page;
    const pageSize = asPositiveNumber(payload.page_size) ?? params.pageSize;
    const total = asNonNegativeNumber(payload.total) ?? skills.length;
    const totalPages = asPositiveNumber(payload.total_pages) ?? Math.max(1, Math.ceil(total / pageSize));
    return { page, pageSize, skills, total, totalPages };
}

export async function deleteWorkspaceManagedSkill(params: {
    workspaceId: string;
    skillId: string;
}): Promise<void> {
    await invokeWorkspaceSkillsFunction({
        action: 'delete',
        workspace_id: params.workspaceId,
        skill_id: params.skillId,
    });
}

export async function getWorkspaceManagedSkillDetail(params: {
    workspaceId: string;
    skillId: string;
}): Promise<WorkspaceManagedSkillDetail> {
    const payload = await invokeWorkspaceSkillsFunction<WorkspaceSkillDetailResponse>({
        action: 'detail',
        workspace_id: params.workspaceId,
        skill_id: params.skillId,
    });
    const skillId = asString(payload.skill_id);
    const skillMarkdown = asString(payload.skill_md);
    const versionSeq = asPositiveNumber(payload.version_seq);
    if (!skillId || skillId !== params.skillId || skillMarkdown === null || !versionSeq) {
        throw new Error('Workspace skill details are unavailable.');
    }
    return { skillId, skillMarkdown, versionSeq };
}
